/**
 * De sollicitatieprocedure als tijdlijn die met het scrollen meeloopt.
 *
 * Eén verticale lijn met een gele vulling die uitgroeit terwijl je langs de stappen leest.
 * Het nummer van een stap die je gepasseerd bent kleurt geel, de stap waar je nu bent
 * krijgt een ring, en wat nog komt blijft gedempt.
 *
 * Het script zet alleen standen -- data-steps-status en data-steps-current -- en de maten
 * van de lijn. Hoe die standen eruitzien staat in common/vacancy-steps.scss.
 *
 * Naar de Osmo Supply "Step-by-step timeline"; de data-attributen komen daarvandaan.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function initVacancySteps() {
  document.querySelectorAll('[data-steps-init]').forEach(setup);
}

function setup(root) {
  const line = root.querySelector('[data-steps-line]');
  const fill = root.querySelector('[data-steps-fill]');
  const items = [...root.querySelectorAll('[data-steps-item]')];

  if (! line || ! fill || ! items.length) {
    return;
  }

  const markers = items.map((item) => item.querySelector('[data-steps-marker]') || item);
  const laatste = items.length - 1;

  // Waar in beeld een stap aan de beurt is: 0 is de bovenrand, 1 de onderrand. Iets onder
  // het midden leest prettiger dan precies het midden -- de stap is dan al gelezen op het
  // moment dat hij oplicht.
  const opgegeven = parseFloat(root.dataset.stepsActivation);
  const activatie = Number.isNaN(opgegeven) ? 0.5 : Math.min(Math.max(opgegeven, 0), 1);
  const grens = `${activatie * 100}%`;

  // Waar elk nummer ligt, als deel van de lijn. Daarmee is uit de voortgang van de
  // scroll af te leiden welke stap aan de beurt is.
  let breuken = [0];

  // De lijn loopt van hart tot hart van het eerste en laatste nummer, en niet van boven
  // naar beneden door het hele vak: anders steekt hij boven de eerste en onder de laatste
  // stap uit.
  function meet() {
    if (items.length < 2) {
      line.style.height = '0px';
      breuken = [0];

      return;
    }

    const basis = line.parentElement.getBoundingClientRect().top;
    const harten = markers.map((marker) => {
      const vak = marker.getBoundingClientRect();

      return vak.top + vak.height / 2 - basis;
    });

    const begin = harten[0];
    const lengte = harten[laatste] - begin;

    line.style.top = `${begin}px`;
    line.style.height = `${lengte}px`;

    breuken = harten.map((hart) => (lengte > 0 ? (hart - begin) / lengte : 0));
  }

  // -2 als beginwaarde en niet -1: -1 is een geldige stand (nog niets bereikt), en die
  // moet de eerste keer wel doorgezet worden.
  let huidig = -2;

  function zet(index) {
    if (index === huidig) {
      return;
    }

    huidig = index;

    items.forEach((item, i) => {
      item.dataset.stepsStatus = index >= 0 && i <= index ? 'active' : 'inactive';
      item.toggleAttribute('data-steps-current', i === index);
    });
  }

  function indexVoor(bereikt, voortgang) {
    if (! bereikt) {
      return -1;
    }

    let index = 0;

    // De marge vangt het afronden op: precies op een nummer komt de voortgang er soms een
    // fractie onder uit, en dan springt de stap één te laat aan.
    breuken.forEach((breuk, i) => {
      if (voortgang + 0.0001 >= breuk) {
        index = i;
      }
    });

    return index;
  }

  function volg(self) {
    zet(indexVoor(self.isActive || self.progress >= 1, self.progress));
  }

  zet(-1);
  gsap.set(fill, { transformOrigin: 'top', scaleY: 0 });

  const media = gsap.matchMedia();

  media.add('(prefers-reduced-motion: no-preference)', () => {
    meet();

    // Opnieuw meten bij elke refresh van ScrollTrigger: de hoogte van de stappen verandert
    // mee met de breedte van het scherm en met de webfonts.
    ScrollTrigger.addEventListener('refreshInit', meet);

    if (items.length > 1) {
      gsap.fromTo(fill, { scaleY: 0 }, {
        scaleY: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: line,
          start: `top ${grens}`,
          end: `bottom ${grens}`,
          scrub: true,
          onUpdate: volg,
          onToggle: volg,
          onRefresh: volg,
        },
      });
    } else {
      zet(0);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => ScrollTrigger.refresh());
    }

    return () => ScrollTrigger.removeEventListener('refreshInit', meet);
  });

  // Wie minder beweging heeft gevraagd krijgt de eindstand: de lijn vol, alle stappen
  // even leesbaar. Dat is dezelfde stand als zonder JavaScript.
  media.add('(prefers-reduced-motion: reduce)', () => {
    meet();
    gsap.set(fill, { scaleY: 1 });
    zet(laatste);
  });
}
